import React, { useState } from "react";

interface UpgradeSuccessBannerProps {
  onDismiss: () => void;
}

const UNLOCKED_FEATURES = [
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M9 1.5L11.09 6.26L16.5 7.09L12.75 10.74L13.68 16.5L9 13.93L4.32 16.5L5.25 10.74L1.5 7.09L6.91 6.26L9 1.5Z" fill="#0088FF" stroke="#0088FF" strokeWidth="1.1" strokeLinejoin="round"/>
      </svg>
    ),
    title: "Unlimited dreams",
    desc: "No cap — chase every dream on your list.",
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="7" stroke="#0088FF" strokeWidth="1.3"/>
        <path d="M9 5v4l3 2" stroke="#0088FF" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
    title: "Unlimited goals & milestones",
    desc: "Break every dream into as many steps as you need.",
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M9 2c-.5 2-2.5 3.5-5 4 2.5.5 4.5 2 5 4 .5-2 2.5-3.5 5-4-2.5-.5-4.5-2-5-4Z" fill="#0088FF" stroke="#0088FF" strokeWidth="0.8" strokeLinejoin="round"/>
      </svg>
    ),
    title: "AI-powered planning",
    desc: "Your personal AI coach builds a milestone roadmap for you.",
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="3" width="14" height="13" rx="2" stroke="#0088FF" strokeWidth="1.3"/>
        <path d="M2 7h14M6 2v2M12 2v2" stroke="#0088FF" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
    title: "Month & year planner views",
    desc: "See your full journey laid out across months and years.",
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="6" cy="9" r="3.5" stroke="#0088FF" strokeWidth="1.3"/>
        <circle cx="12" cy="9" r="3.5" stroke="#0088FF" strokeWidth="1.3"/>
      </svg>
    ),
    title: "Unlimited categories",
    desc: "Organise your dreams your way without any limits.",
  },
];

/**
 * Shown on Dashboard after a successful Stripe checkout redirect.
 * Full celebratory modal — replaces the thin banner.
 */
export default function UpgradeSuccessBanner({ onDismiss }: UpgradeSuccessBannerProps) {
  const [visible, setVisible] = useState(true);

  const handleDismiss = () => {
    setVisible(false);
    onDismiss();
  };

  if (!visible) return null;

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(17,49,99,0.55)", backdropFilter: "blur(4px)" }}
      onClick={handleDismiss}
    >
      {/* Modal card */}
      <div
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl"
        style={{ boxShadow: "0 24px 64px rgba(0,136,255,0.18)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Gradient header */}
        <div
          className="flex flex-col items-center rounded-t-2xl px-8 pt-10 pb-8 text-center"
          style={{ background: "linear-gradient(135deg, #0088FF 0%, #0055CC 100%)" }}
        >
          {/* Badge icon */}
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/20">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M16 3L19.5 11.5L29 12.9L22.5 19.2L24.1 29L16 24.6L7.9 29L9.5 19.2L3 12.9L12.5 11.5L16 3Z" fill="white" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="m-0 text-[22px] font-bold leading-snug text-white">
            You are now Premium!
          </h2>
          <p className="m-0 mt-2 text-[14px] leading-relaxed text-white/85">
            Every dream you have deserves to be pursued.<br/>
            You have just taken the most important step — committing to making them real.
          </p>
        </div>

        {/* Features list */}
        <div className="px-7 py-6">
          <p className="m-0 mb-4 text-[11px] font-bold uppercase tracking-widest" style={{ color: "#0088FF" }}>
            Everything now unlocked for you
          </p>
          <ul className="m-0 flex flex-col gap-3 p-0" style={{ listStyle: "none" }}>
            {UNLOCKED_FEATURES.map((f) => (
              <li key={f.title} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg" style={{ background: "rgba(0,136,255,0.08)" }}>
                  {f.icon}
                </span>
                <div>
                  <p className="m-0 text-[13px] font-semibold text-slate-800">{f.title}</p>
                  <p className="m-0 text-[12px] leading-snug text-slate-500">{f.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <div className="px-7 pb-7">
          <button
            type="button"
            onClick={handleDismiss}
            className="w-full rounded-xl py-3 text-[14px] font-bold text-white transition-colors"
            style={{ background: "linear-gradient(135deg, #0088FF 0%, #0055CC 100%)", border: "none", cursor: "pointer" }}
          >
            Start building my dreams
          </button>
          <p className="m-0 mt-3 text-center text-[11px]" style={{ color: "#9CA3AF" }}>
            Manage your subscription anytime from Settings
          </p>
        </div>

        {/* Close X */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-lg border-0 bg-white/20 transition-colors hover:bg-white/30"
          style={{ cursor: "pointer" }}
        >
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
            <path d="M1.5 1.5L9.5 9.5M9.5 1.5L1.5 9.5" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
}
